import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AuthService} from '@app/auth/auth.service';

import {deserialize} from '@app/serializable';
import {StorageService} from '@app/shared/storage.service';
import {StateService} from '@app/state/state.service';

import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, map, take, tap} from 'rxjs/operators';

export const PLACEHOLDER_NAME = 'aaa';

export class SiteConfig {
    id: string;
    client_id: string;
    data: any;
    thumbnail: string;
    slack: SlackState;
    get displayName(): string {
        return this.data.name.replace(PLACEHOLDER_NAME, '');
    }
}

export enum SetupState {
    Name,
    Stream,
    Invites,
    Complete,
}

export class SlackState {
    configuration: any;
    installed: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class SiteConfigService {
    public siteConfig: ReplaySubject<SiteConfig>;
    public hasRequested: boolean;

    constructor(
        private stateService: StateService,
        private storageService: StorageService,
        private authService: AuthService,
        private router: Router,
        private http: HttpClient,
    ) {
        this.siteConfig = new ReplaySubject<SiteConfig>(1);
        const stateObservable = this.stateService.state;
        const requestObservable = this.http.get<SiteConfig>('/api/workspace/client/config/');
        this.hasRequested = false;

        if (this.storageService.get('token')) {
            combineLatest([stateObservable, requestObservable])
                .pipe(
                    catchError((err) => {
                        if (err.status === 403) {
                            // Log them out
                            this.authService.logout().subscribe(() => {
                                this.router.navigate(['/']);
                            });
                        }
                        return of([null, null]);
                    }),
                    take(1),
                )
                .subscribe(([state, config]) => {
                    if (config !== null) {
                        this.siteConfig.next(deserialize(SiteConfig, config));
                    }
                    this.hasRequested = true;
                });
        }
    }

    get(): Observable<SiteConfig> {
        return this.http.get<SiteConfig>('/api/workspace/client/config/').pipe(
            tap((config) => {
                this.siteConfig.next(deserialize(SiteConfig, config));
                this.hasRequested = true;
            }),
        );
    }

    save(data: any): Observable<SiteConfig> {
        const options = {
            data,
        };
        return this.http.put<SiteConfig>('/api/workspace/client/config/', options).pipe(
            map((result) => deserialize(SiteConfig, result)),
            tap((config) => this.siteConfig.next(config)),
        );
    }

    saveThumbnail(file: File): Observable<SiteConfig> {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.put<SiteConfig>('/api/workspace/client/config/', formData).pipe(
            map((result) => deserialize(SiteConfig, result)),
            tap((config) => this.siteConfig.next(config)),
        );
    }
}
